import React, { useState } from 'react';
import { X, Upload, FileText, CheckCircle, Loader2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { BlobServiceClient } from '@azure/storage-blob';

const ApplicationForm = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    country: '',
    program: '',
    course: ''
  });
  const [showDocuments, setShowDocuments] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [uploadingDocs, setUploadingDocs] = useState({});
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  // Azure Storage configuration
  const AZURE_STORAGE_URL = 'https://xpertstgacc.blob.core.windows.net/xps?sp=racwdli&st=2026-03-07T11:57:27Z&se=2026-03-07T20:12:27Z&spr=https&sv=2024-11-04&sr=c&sig=DzWcY4cR%2FqxiPawwNjjkcSgPqrcGXTExkJx3ixSquoE%3D';

  const requiredDocuments = [
    'All Transcripts',
    'Consolidated Marks Memo',
    'Provisional Certificate',
    'Original Degree',
    'Letter of Recommendations - 3 from College',
    'English Test Scorecard - IELTS / Duolingo / TOEFL / PTE',
    'Passport First and Last Page',
    '10th and 12th Certificate',
    'Resume',
    'All Semester Memos',
    'Medium Of Instruction - From College',
    'No Back Log Certificate - From College'
  ];

  const uploadToAzure = async (file, docName) => {
    try {
      // Create a sanitized folder name from student name
      const folderName = formData.name
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');

      if (!folderName) {
        throw new Error('Please fill in your name first');
      }

      console.log('Uploading file:', file.name, 'for document:', docName);
      console.log('Folder name:', folderName);

      // Parse the container SAS URL
      const containerUrl = AZURE_STORAGE_URL;
      
      // Create a unique blob name with student folder
      const timestamp = new Date().getTime();
      const fileExtension = file.name.split('.').pop();
      const sanitizedDocName = docName.replace(/[^a-z0-9]/gi, '_');
      const blobName = `${folderName}/${sanitizedDocName}_${timestamp}.${fileExtension}`;

      console.log('Blob name:', blobName);

      // Construct the full blob URL with SAS token
      const blobUrl = `${containerUrl.split('?')[0]}/${blobName}?${containerUrl.split('?')[1]}`;
      
      console.log('Blob URL (without token):', blobUrl.split('?')[0]);

      // Create BlockBlobClient directly with the blob URL
      const { BlockBlobClient } = await import('@azure/storage-blob');
      const blockBlobClient = new BlockBlobClient(blobUrl);

      console.log('Starting upload...');

      // Upload file using uploadData
      const uploadResponse = await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: {
          blobContentType: file.type
        }
      });

      console.log('Upload successful!', uploadResponse);

      return {
        url: blobUrl.split('?')[0],
        name: file.name,
        blobName: blobName
      };
    } catch (error) {
      console.error('Error uploading to Azure:', error);
      console.error('Error message:', error.message);
      console.error('Error details:', error);
      
      // Provide more specific error messages
      if (error.message.includes('Failed to fetch')) {
        throw new Error('CORS Error: Azure Storage needs to allow requests from localhost. Please configure CORS settings in Azure Portal.');
      } else if (error.statusCode === 403) {
        throw new Error('Permission denied: Check SAS token permissions');
      } else if (error.statusCode === 404) {
        throw new Error('Container not found: Verify container name');
      }
      
      throw error;
    }
  };

  const handleFileUpload = async (docName, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'Please upload a file smaller than 5MB',
      });
      return;
    }

    // Store file locally
    setUploadedDocs(prev => ({
      ...prev,
      [docName]: file
    }));

    toast.success('File Selected', {
      description: `${file.name} ready to upload`,
    });
  };

  const handleSaveDocuments = async () => {
    // Check if name is filled
    if (!formData.name.trim()) {
      toast.error('Name Required', {
        description: 'Please fill in your name before uploading documents',
      });
      setShowDocuments(false);
      return;
    }

    const docsToUpload = Object.entries(uploadedDocs);
    
    if (docsToUpload.length === 0) {
      toast.error('No Documents', {
        description: 'Please select at least one document to upload',
      });
      return;
    }

    // Set uploading state
    setUploadingDocs({ uploading: true });

    try {
      toast.info('Uploading Documents', {
        description: `Uploading ${docsToUpload.length} document(s) to Azure Storage...`,
      });

      // Upload all documents to Azure
      const uploadPromises = docsToUpload.map(([docName, file]) => 
        uploadToAzure(file, docName)
      );

      const results = await Promise.all(uploadPromises);

      // Update uploaded docs with Azure URLs
      const updatedDocs = {};
      docsToUpload.forEach(([docName, file], index) => {
        updatedDocs[docName] = {
          file: file,
          azureUrl: results[index].url,
          blobName: results[index].blobName
        };
      });

      setUploadedDocs(updatedDocs);

      console.log('All documents uploaded successfully:', updatedDocs);
      
      setShowDocuments(false);
      
      // Show success dialog
      setShowSuccessDialog(true);
    } catch (error) {
      console.error('Upload error details:', error);
      toast.error('Upload Failed', {
        description: error.message || 'Failed to upload documents to Azure Storage. Please try again.',
      });
      console.error('Upload error:', error);
    } finally {
      setUploadingDocs({});
    }
  };

  const handleFileUpload_OLD = (docName, event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedDocs(prev => ({
        ...prev,
        [docName]: file
      }));
      toast.success('File Uploaded', {
        description: `${docName} uploaded successfully`,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/submit-application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
        // Show success message
        toast.success(result.message || 'Application Submitted!', {
          description: 'We will contact you shortly with more information.',
        });
        // Reset form
        setFormData({
          name: '',
          phone: '',
          email: '',
          country: '',
          program: '',
          course: ''
        });
        // Close dialog
        onOpenChange(false);
      } else {
        toast.error('Submission Failed', {
          description: 'Please try again later.',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Submission Failed', {
        description: 'Please check your connection and try again.',
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCloseSuccess = () => {
    setShowSuccessDialog(false);
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      country: '',
      program: '',
      course: ''
    });
    setUploadedDocs({});
    // Close main dialog
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-brand-500 bg-clip-text text-transparent">
            Apply Now
          </DialogTitle>
          <DialogDescription>
            Fill in your details and we'll help you start your international education journey.
          </DialogDescription>
        </DialogHeader>

        {!showDocuments ? (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </div>

            {/* Phone Number */}
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number *</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="Enter your phone number"
                value={formData.phone}
                onChange={(e) => handleChange('phone', e.target.value)}
                required
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email">Email ID *</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email address"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                required
              />
            </div>

            {/* Country of Interest */}
            <div className="space-y-2">
              <Label htmlFor="country">Country of Interest *</Label>
              <Input
                id="country"
                type="text"
                placeholder="Enter your preferred country"
                value={formData.country}
                onChange={(e) => handleChange('country', e.target.value)}
                required
              />
            </div>

            {/* Program Interested */}
            <div className="space-y-2">
              <Label htmlFor="program">Program Interested *</Label>
              <Select value={formData.program} onValueChange={(value) => handleChange('program', value)} required>
                <SelectTrigger id="program">
                  <SelectValue placeholder="Select program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bachelors">Bachelors</SelectItem>
                  <SelectItem value="masters">Masters</SelectItem>
                  <SelectItem value="mbbs">MBBS</SelectItem>
                  <SelectItem value="phd">PhD</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Course Interested */}
            <div className="space-y-2">
              <Label htmlFor="course">Course Interested *</Label>
              <Input
                id="course"
                type="text"
                placeholder="e.g., Computer Science, Business Administration"
                value={formData.course}
                onChange={(e) => handleChange('course', e.target.value)}
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => setShowDocuments(true)}
                className="flex-1 bg-gradient-to-r from-navy-700 to-navy-900 hover:from-navy-900 hover:to-navy-900 text-white"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Documents
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-brand-500 hover:bg-brand-600 text-white"
              >
                Submit Application
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Required Documents</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDocuments(false)}
              >
                Back to Form
              </Button>
            </div>

            <div className="bg-navy-50 border border-navy-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-navy-800">
                Please upload all required documents. Accepted formats: PDF, JPG, PNG (Max 5MB per file)
              </p>
            </div>

            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
              {requiredDocuments.map((docName, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-brand-300 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1">
                    {uploadedDocs[docName] ? (
                      <CheckCircle className="w-5 h-5 text-brand-500 flex-shrink-0" />
                    ) : (
                      <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{docName}</p>
                      {uploadedDocs[docName] && (
                        <p className="text-xs text-brand-600">
                          {uploadedDocs[docName].file ? uploadedDocs[docName].file.name : uploadedDocs[docName].name}
                        </p>
                      )}
                    </div>
                  </div>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileUpload(docName, e)}
                      disabled={uploadingDocs.uploading}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant={uploadedDocs[docName] ? "outline" : "default"}
                      className={uploadedDocs[docName] ? "" : "bg-brand-500 hover:bg-brand-600"}
                      onClick={(e) => e.currentTarget.previousElementSibling.click()}
                      disabled={uploadingDocs.uploading}
                    >
                      {uploadedDocs[docName] ? 'Change' : 'Upload'}
                    </Button>
                  </label>
                </div>
              ))}
            </div>

            <div className="flex gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowDocuments(false)}
                className="flex-1"
                disabled={uploadingDocs.uploading}
              >
                Back to Form
              </Button>
              <Button
                type="button"
                onClick={handleSaveDocuments}
                className="flex-1 bg-brand-500 hover:bg-brand-600 text-white"
                disabled={uploadingDocs.uploading}
              >
                {uploadingDocs.uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading to Azure...
                  </>
                ) : (
                  'Save Documents'
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    {/* Success Dialog */}
    <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
      <DialogContent className="sm:max-w-[450px]">
        <div className="flex flex-col items-center justify-center py-6">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-navy-700 to-navy-900 rounded-full flex items-center justify-center mb-6 animate-bounce">
            <Check className="w-10 h-10 text-white" strokeWidth={3} />
          </div>

          {/* Success Message */}
          <DialogTitle className="text-2xl font-bold text-center mb-2">
            Application Successfully Submitted!
          </DialogTitle>
          
          <DialogDescription className="text-center text-gray-600 mb-6">
            Thank you, {formData.name}! Your application and documents have been uploaded successfully. 
            Our team will review your application and contact you shortly.
          </DialogDescription>

          {/* Application Details */}
          <div className="w-full bg-gray-50 rounded-lg p-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Email:</span>
              <span className="font-medium text-gray-900">{formData.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Program:</span>
              <span className="font-medium text-gray-900 capitalize">{formData.program}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Documents Uploaded:</span>
              <span className="font-medium text-brand-600">{Object.keys(uploadedDocs).length} files</span>
            </div>
          </div>

          {/* Close Button */}
          <Button
            onClick={handleCloseSuccess}
            className="w-full bg-brand-500 hover:bg-brand-600 text-white"
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </>
  );
};

export default ApplicationForm;
